import { Download } from 'lucide-react'
import { useEffect, useState } from 'react'

import { ButtonLink } from 'slab'
import type { PayrollPayPeriod } from 'utils/web_api.schemas'

import type { DeductionTableRow } from '../../types/deductions'
import type { PaySchedule } from '../../types/payroll'
import { getDateString } from '../../utils'
import { DownloadButton } from '../reusable/table/DownloadButton'

const baseHeaders = [
  { label: 'First Name', key: 'givenName' },
  { label: 'Last Name', key: 'familyName' },
  { label: 'Section 125 Deduction', key: 's125Amount' },
  { label: 'Posttax Deduction', key: 'posttaxAmount' },
  { label: 'Employer Contribution', key: 'employerContribution' },
  { label: 'Employer Allowance', key: 'allowance' },
  { label: 'Total Premium Cost', key: 'planCost' },
  { label: 'Coverage Start Date', key: 'coverageStartDate' },
  { label: 'Coverage End Date', key: 'coverageEndDate' },
  { label: 'Source ID', key: 'sourceId' },
]

// Additional CSV headers need to be added after existing headers to avoid causing issues for users
// who programmatically use these reports and rely on a specific column order
const additionalHeaders = [
  { label: 'Pay Date', key: 'periodPayDate' },
  { label: 'Employee ID', key: 'employeeId' },
]

const sensitiveCsvHeaders = [{ label: 'SSN', key: 'ssn' }]
// Below headers are used for Royal American. Break these up into a separate set of headers if other employers require this data.
const expandedCsvHeaders = [
  { label: 'Metadata Key 0', key: 'metadataKey0' },
  { label: 'Metadata Value 0', key: 'metadataValue0' },
  { label: 'Metadata Key 1', key: 'metadataKey1' },
  { label: 'Metadata Value 1', key: 'metadataValue1' },
  { label: 'Metadata Key 2', key: 'metadataKey2' },
  { label: 'Metadata Value 2', key: 'metadataValue2' },
  { label: 'Metadata Key 3', key: 'metadataKey3' },
  { label: 'Metadata Value 3', key: 'metadataValue3' },
  { label: 'Metadata Key 4', key: 'metadataKey4' },
  { label: 'Metadata Value 4', key: 'metadataValue4' },
  { label: 'Metadata Key 5', key: 'metadataKey5' },
  { label: 'Metadata Value 5', key: 'metadataValue5' },
  { label: 'Metadata Key 6', key: 'metadataKey6' },
  { label: 'Metadata Value 6', key: 'metadataValue6' },
  { label: 'Metadata Key 7', key: 'metadataKey7' },
  { label: 'Metadata Value 7', key: 'metadataValue7' },
  { label: 'Metadata Key 8', key: 'metadataKey8' },
  { label: 'Metadata Value 8', key: 'metadataValue8' },
  { label: 'Metadata Key 9', key: 'metadataKey9' },
  { label: 'Metadata Value 9', key: 'metadataValue9' },
]

const DownloadDeductionsButton = ({
  deductions,
  selectedPaySchedule,
  selectedPeriodData,
  includeSsn,
  includeSourceId,
  showSecondarySourceIdAsColumn,
  employerId,
  payScheduleId,
  enableGeneratedReportDownload = false,
}: {
  deductions: DeductionTableRow[]
  selectedPaySchedule: PaySchedule | undefined
  selectedPeriodData: PayrollPayPeriod | undefined
  includeSsn: boolean
  includeSourceId: boolean
  showSecondarySourceIdAsColumn: boolean
  employerId: string
  payScheduleId: string
  enableGeneratedReportDownload?: boolean
}) => {
  const [sortedData, setSortedData] = useState<DeductionTableRow[]>([])

  useEffect(() => {
    const sorted = [...deductions].sort((a, b) => {
      return (
        a.familyName.localeCompare(b.familyName) ||
        a.givenName.localeCompare(b.givenName)
      )
    })
    setSortedData(sorted)
  }, [deductions])

  const headers = [
    ...baseHeaders,
    ...(includeSsn ? sensitiveCsvHeaders : []),
    ...(showSecondarySourceIdAsColumn
      ? [{ label: 'Secondary Source ID', key: 'secondarySourceId' }]
      : []),
    ...(includeSourceId ? expandedCsvHeaders : []),
    ...additionalHeaders
  ]
  // Check if we have a server-generated report with download URL and the flag is enabled
  const hasServerReport = selectedPeriodData?.latest_report?.download_url
  const shouldUseServerReport = hasServerReport && enableGeneratedReportDownload

  if (shouldUseServerReport) {
    // Use server-generated report for download
    return (
      <ButtonLink
        href={selectedPeriodData.latest_report!.download_url}
        download
        disabled={deductions.length === 0}
        leftIcon={Download}
        variant='primary'
      >
        Download
      </ButtonLink>
    )
  }

  // Fall back to client-side CSV generation
  const filename =
    selectedPaySchedule && selectedPeriodData
      ? `${employerId}-${payScheduleId}-${getDateString(
        new Date(`${selectedPeriodData?.pay_date || ''}`)
      )}.csv`
      : `${employerId}-payroll_deductions.csv`
  return (
    <DownloadButton
      data={sortedData}
      filename={filename}
      headers={headers}
      disabled={deductions.length === 0}
      linkClassNames='has-[:disabled]:cursor-default'
    />
  )
}

export default DownloadDeductionsButton
